'use client';

/**
 * <ListingFilterSheet> (BAL-665) — the mobile filter surface: a sticky filter
 * button (badge = active-filter count) opening a full-screen scrollable sheet
 * with the facets + a Reset / Apply footer. Sort lives in the sheet too. The
 * Meteor QPageSticky → QDialog pattern; the eBay/Shopee mobile standard.
 */
import type { ReactNode } from 'react';

import { SlidersHorizontalIcon } from 'lucide-react';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@kit/ui/sheet';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { FacetControl } from './facet-control';
import type { ListingFilterConfig } from './types';
import type { UseListingFiltersResult } from './use-listing-filters';

interface ListingFilterSheetProps {
  config: ListingFilterConfig;
  filters: UseListingFiltersResult;
  /** Surface-specific controls (entity pickers) — see <ListingFilterBar>. */
  leading?: ReactNode;
  /** Surface-specific control (e.g. personalization toggle). */
  slot?: ReactNode;
  className?: string;
}

export function ListingFilterSheet({
  config,
  filters,
  leading,
  slot,
  className,
}: ListingFilterSheetProps) {
  const { state, setSort, toggleFacet, setFacet, activeCount, clearAll } =
    filters;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className={cn('gap-2', className)}>
          <SlidersHorizontalIcon className="size-4" />
          <Trans i18nKey="common:filters.title" />
          {activeCount > 0 ? (
            <Badge variant="secondary" className="px-1.5">
              {activeCount}
            </Badge>
          ) : null}
        </Button>
      </SheetTrigger>

      <SheetContent side="bottom" className="flex max-h-[85dvh] flex-col">
        <SheetHeader>
          <SheetTitle>
            <Trans i18nKey="common:filters.title" />
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-5 py-2">
            {leading ? <div className="flex flex-col gap-2">{leading}</div> : null}

            {config.sorts.length > 0 ? (
              <div className="flex flex-col gap-2">
                <Label className="text-xs">
                  <Trans i18nKey="common:sortBy" defaults="Sort by" />
                </Label>
                <Select value={state.sort} onValueChange={setSort}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config.sorts.map((sort) => (
                      <SelectItem key={sort.value} value={sort.value}>
                        {sort.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {config.facets.map((facet) => (
              <div key={facet.key} className="flex flex-col gap-2">
                <Label className="text-xs">{facet.label}</Label>
                <FacetControl
                  facet={facet}
                  values={state.facets[facet.key] ?? []}
                  onToggle={(value) => toggleFacet(facet.key, value)}
                  onSetValues={(values) => setFacet(facet.key, values)}
                  block
                />
              </div>
            ))}

            {slot ? <div className="flex flex-col gap-2">{slot}</div> : null}
          </div>
        </div>

        <SheetFooter className="flex-row gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={clearAll}
            disabled={activeCount === 0}
          >
            <Trans i18nKey="common:reset" />
          </Button>
          {/* Apply just closes — state is already live in the URL. */}
          <SheetTrigger asChild>
            <Button className="flex-1">
              <Trans i18nKey="common:apply" defaults="Apply" />
            </Button>
          </SheetTrigger>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
